import { Alert, Button, Stack, Typography } from '@mui/material';
import { EllipsesMenu } from 'src/components';
import type { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import type { BrandWithSupport } from 'src/graphql';
import { ClientSegmentSelector } from 'src/pages/Settings/memberships/components/client-segment-selector';
import { ConfigSurfaceSelect } from 'src/pages/Settings/memberships/components/config-surface-select';
import { ExportPricingButton } from 'src/pages/Settings/memberships/components/export-pricing-button';
import { PanelHeader, PanelTitle } from 'src/pages/Settings/memberships/styled';
import { configPanelTitleRowSx } from 'src/pages/Settings/memberships/styles';
import type { ConfigSurface } from 'src/pages/Settings/memberships/types';

// `ExportPricingButton` references this via aria-describedby.
const PANEL_TITLE_ID = 'memberships-config-panel-title';

type MembershipsConfigPanelHeaderProps = {
  showSurfaceSelect: boolean;
  activeSurface: ConfigSurface;
  onSurfaceChange: (surface: ConfigSurface) => void;
  isLandingPageSurface: boolean;
  segments: BrandWithSupport[];
  segmentsLoading: boolean;
  selectedSegmentId: string | null;
  onSegmentChange: (segmentId: string | null) => Promise<boolean>;
  showExportButton: boolean;
  exportDisabled: boolean;
  isExporting: boolean;
  onExportClick: () => void;
  showMenu: boolean;
  menuOptions: EllipsesMenuOption[];
  showReadOnlyWarning: boolean;
  refreshErrorMessage: string | null;
  onRetryRefresh: () => void;
};

export const MembershipsConfigPanelHeader = ({
  showSurfaceSelect,
  activeSurface,
  onSurfaceChange,
  isLandingPageSurface,
  segments,
  segmentsLoading,
  selectedSegmentId,
  onSegmentChange,
  showExportButton,
  exportDisabled,
  isExporting,
  onExportClick,
  showMenu,
  menuOptions,
  showReadOnlyWarning,
  refreshErrorMessage,
  onRetryRefresh,
}: MembershipsConfigPanelHeaderProps) => (
  <PanelHeader>
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={configPanelTitleRowSx}
      >
        <PanelTitle id={PANEL_TITLE_ID}>
          {isLandingPageSurface ? 'Landing Page' : 'Configuration'}
        </PanelTitle>
        {showExportButton || showMenu ? (
          <Stack direction="row" alignItems="center" gap={0.5}>
            {showExportButton ? (
              <ExportPricingButton
                disabled={exportDisabled}
                isExporting={isExporting}
                onClick={onExportClick}
                ariaLabel="Export memberships configuration"
                ariaDescribedBy={PANEL_TITLE_ID}
              />
            ) : null}
            {showMenu ? (
              <EllipsesMenu
                triggerTestId="memberships-config-menu"
                options={menuOptions}
                icon="more-vertical"
                iconLibrary="lucide"
                title="More actions"
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <ClientSegmentSelector
        segments={segments}
        loading={segmentsLoading}
        selectedSegmentId={selectedSegmentId}
        onSegmentChange={onSegmentChange}
      />
      {showSurfaceSelect ? (
        <ConfigSurfaceSelect value={activeSurface} onChange={onSurfaceChange} />
      ) : null}
      {showReadOnlyWarning ? (
        <Alert severity="warning">
          <Typography variant="subtitle2">Read-only mode</Typography>
          <Typography variant="body2">
            You don&apos;t have permission to edit membership configuration.
          </Typography>
        </Alert>
      ) : null}
      {refreshErrorMessage ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetryRefresh}>
              Retry refresh
            </Button>
          }
        >
          <Typography variant="body2">{refreshErrorMessage}</Typography>
        </Alert>
      ) : null}
    </Stack>
  </PanelHeader>
);
